import axios from 'axios';
import { googleLogout } from "@react-oauth/google";
import {
  USER_LOGIN_REQUEST,
  USER_LOGIN_SUCCESS,
  USER_LOGIN_FAIL,
  USER_LOGOUT,
  USER_REGISTER_REQUEST,
  USER_REGISTER_SUCCESS,
  USER_REGISTER_FAIL,
  USER_DETAILS_REQUEST,
  USER_DETAILS_SUCCESS,
  USER_DETAILS_FAIL,
  USER_DETAILS_RESET,
  USER_UPDATE_PROFILE_REQUEST,
  USER_UPDATE_PROFILE_SUCCESS,
  USER_UPDATE_PROFILE_FAIL,
  // USER_UPDATE_PROFILE_RESET,
  USER_UPDATE_PROFILE_INFO_REQUEST,
  USER_UPDATE_PROFILE_INFO_SUCCESS,
  USER_UPDATE_PROFILE_INFO_FAIL,
  USER_UPDATE_PROFILE_IMAGE_REQUEST,
  USER_UPDATE_PROFILE_IMAGE_SUCCESS,
  USER_UPDATE_PROFILE_IMAGE_FAIL,
  // USER_UPDATE_PROFILE_IMAGE_RESET,
  USER_FOLLOW_REQUEST,
  USER_FOLLOW_SUCCESS,
  USER_FOLLOW_FAIL,
  USER_UNFOLLOW_REQUEST,
  USER_UNFOLLOW_SUCCESS,
  USER_UNFOLLOW_FAIL,
  USER_LIST_REQUEST,
  USER_LIST_SUCCESS,
  USER_LIST_FAIL,
  USER_LIST_RESET,
  USER_DELETE_REQUEST,
  USER_DELETE_SUCCESS,
  USER_DELETE_FAIL,
  USER_UPDATE_REQUEST,
  USER_UPDATE_SUCCESS,
  USER_UPDATE_FAIL,
  USER_VALIDATE_PROFILE_REQUEST,
  USER_VALIDATE_PROFILE_SUCCESS,
  USER_VALIDATE_PROFILE_FAIL,
  // USER_VALIDATE_PROFILE_RESET,
  USER_SEND_MAIL_PROFILE_REQUEST,
  USER_SEND_MAIL_PROFILE_SUCCESS,
  USER_SEND_MAIL_PROFILE_FAIL,
  // USER_SEND_MAIL_PROFILE_RESET,
  USER_CHANGE_PASS_PROFILE_REQUEST,
  USER_CHANGE_PASS_PROFILE_SUCCESS,
  USER_CHANGE_PASS_PROFILE_FAIL,
  // USER_CHANGE_PASS_PROFILE_RESET,
  USER_SEND_MAIL_CHANGE_PASS_REQUEST,
  USER_SEND_MAIL_CHANGE_PASS_SUCCESS,
  USER_SEND_MAIL_CHANGE_PASS_FAIL,
  // USER_SEND_MAIL_CHANGE_PASS_RESET,
  // USER_LOGIN_RESET,
} from '../constants/userConstants';
import {
  AUTH_LOGIN_SUCCESS,
  AUTH_LOGOUT,
  AUTH_IDENTITY_PATCH,
} from '../constants/authConstants';
import api from '../api/axios';
import { resetNotifications } from '../features/notifications/model/notificationActions';
import {
  getOrStartRefresh,
  invalidateAuth,
} from '../auth/refreshCoordinator';
import {
  getProductionAuthPersistence,
  __resetProductionAuthPersistence,
} from '../persistence/authPersistenceProduction';
import { buildUserProfileUpdateFailPayload } from '../components/profile/edit/profileUpdateErrors';

export { buildUserProfileUpdateFailPayload };

const mergeUserInfo = (currentUserInfo = {}, nextUserInfo = {}) => {
  const access =
    nextUserInfo?.access ||
    currentUserInfo?.access;

  return {
    ...currentUserInfo,
    ...nextUserInfo,
    access,
    refresh: nextUserInfo?.refresh || currentUserInfo?.refresh,
  };
};

let authPersistenceOverride = null;

function getAuthPersistence() {
  return authPersistenceOverride || getProductionAuthPersistence();
}

/** @internal P-1C test injection — not for production callers */
export function __setAuthPersistenceForTests(facade) {
  authPersistenceOverride = facade;
}

/** @internal P-1C test injection — not for production callers */
export function __resetAuthPersistenceForTests() {
  authPersistenceOverride = null;
  __resetProductionAuthPersistence();
}

const persistUserInfo = (userInfo) => {
  getAuthPersistence().writeUserInfoBlob(userInfo);
};

/**
 * Session-establishment helper: dual-write canonical login state then persist.
 * On persistence failure, roll back canonical userLogin via USER_LOGOUT before
 * rethrowing so USER_LOGIN_FAIL does not leave authenticated Redux without blob.
 * (USER_LOGIN_FAIL preserves userInfo per B2 re-login invariant.)
 */
const dispatchLoginSuccessAndPersist = (dispatch, data) => {
  dispatch({
    type: USER_LOGIN_SUCCESS,
    payload: data,
  });

  try {
    persistUserInfo(mergeUserInfo({}, data));
  } catch (persistError) {
    dispatch({ type: USER_LOGOUT });
    throw persistError;
  }
};

/**
 * In-session userInfo update helper: persist before path SUCCESS and canonical Redux.
 * On write failure, rethrow to path-specific catch — previous canonical, persisted,
 * and secondary slice states remain stable without USER_LOGOUT.
 *
 * @param {object} [options]
 * @param {object} [options.pathSuccess] - path-specific SUCCESS action (after persist)
 * @param {Function} [options.afterCanonicalUpdate] - e.g. AUTH_IDENTITY_PATCH after USER_LOGIN_SUCCESS
 */
const dispatchPersistedUserInfoUpdate = (dispatch, mergedUserInfo, options = {}) => {
  const { pathSuccess, afterCanonicalUpdate } = options;
  persistUserInfo(mergedUserInfo);
  if (pathSuccess) {
    dispatch(pathSuccess);
  }
  dispatch({
    type: USER_LOGIN_SUCCESS,
    payload: mergedUserInfo,
  });
  if (afterCanonicalUpdate) {
    afterCanonicalUpdate();
  }
};

const clearPersistedUserInfo = () => {
  getAuthPersistence().clearUserInfoBlob();
};


// Thin wrapper preserved for backward compat with any thunk-style consumer.
// Live caller (wsHelper) now invokes getOrStartRefresh directly.
export const refreshToken = () => (dispatch) => getOrStartRefresh(dispatch);

// import { ORDER_LIST_MY_RESET } from '../constants/orderConstants';

export const login = (email, password) => async (dispatch) => {
  try {
    dispatch({
      type: USER_LOGIN_REQUEST,
    });

    const config = {
      headers: {
        'Content-type': 'application/json',
      },
    };

    const { data } = await axios.post(
      '/api/users/login/',
      { email: email, password: password },
      config
    );

    dispatchLoginSuccessAndPersist(dispatch, data);

    // Phase 4A dual-write — auth slice replicates from same payload.
    // CANONICAL source remains state.userLogin.userInfo (above). No await
    // between the two dispatches: same tick, no consumer can observe drift.
    dispatch({
      type: AUTH_LOGIN_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: USER_LOGIN_FAIL,
      payload:
        error.response && error.response.data.detail
          ? error.response.data.detail
          : error.message,
    });
  }
};

export const googleLogin = (credential) => async (dispatch) => {
  try {
    dispatch({
      type: USER_LOGIN_REQUEST,
    });

    const config = {
      headers: {
        "Content-type": "application/json",
      },
    };

    const { data } = await axios.post(
      '/api/users/google/login/',
      { credential },
      config
    );

    dispatchLoginSuccessAndPersist(dispatch, data);

    // Phase 4A dual-write — auth slice replicates from same payload.
    dispatch({
      type: AUTH_LOGIN_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: USER_LOGIN_FAIL,
      payload:
        error.response && error.response.data.detail
          ? error.response.data.detail
          : error.message,
    });
  }
};

export const logout = () => (dispatch) => {
  // ⚠ HARD-RELOAD CONTRACT (Phase 4A):
  //   The terminal `window.location.href = "/login"` below is LOAD-BEARING.
  //   The Redux dispatches in this thunk reset auth/userLogin/userDetails/
  //   userList — but profile slice, bootstrap.phase, refresh coordinator's
  //   `terminated` / `inFlight`, WS connection, and chat slice are NOT
  //   reset here. The full page reload triggered by navigation discards
  //   the entire JS context and re-hydrates from empty localStorage,
  //   implicitly clearing all of the above.
  //
  //   See authReducers.js AUTH_LOGOUT comment for the full inventory of
  //   what hard-reload cleans up. Soft logout (no reload) is NOT supported
  //   today and would require the additional resets enumerated there.

  // Mark auth as terminated FIRST so any in-flight refresh resolution sees
  // the flag at its post-await check and refuses to apply (no Redux dispatch,
  // no localStorage write, no zombie retry, no zombie WS open).
  invalidateAuth();

  try {
    clearPersistedUserInfo();
  } catch {
    // P-1C: do not dispatch logout or navigate when persistence clear fails.
    return;
  }

  dispatch({ type: USER_LOGOUT });
  dispatch({ type: USER_DETAILS_RESET });
  dispatch({ type: USER_LIST_RESET });
  dispatch(resetNotifications());

  // Phase 4A dual-write — reset auth slice. Dispatched before navigation so
  // any in-render observer sees consistent reset across both slices.
  dispatch({ type: AUTH_LOGOUT });

  window.location.href = "/login";
};

export const register = (email, password) => async (dispatch) => {
  try {
    dispatch({
      type: USER_REGISTER_REQUEST,
    });

    const config = {
      headers: {
        'Content-type': 'application/json',
      },
    };

    const { data } = await axios.post(
      '/api/users/register/',
      { email: email, password: password },
      config
    );

    dispatch({
      type: USER_REGISTER_SUCCESS,
      payload: data,
    });

    dispatchLoginSuccessAndPersist(dispatch, data);

    // Phase 4A dual-write — auth slice replicates from same payload.
    //
    // CONTRACT: `data` here is the FINAL NORMALIZED backend response from
    // POST /api/users/register/ (UserSerializerWithToken — full {access,
    // refresh, id, public_id, email, is_email_verified, ...}). It is the
    // SAME ref dispatched to USER_REGISTER_SUCCESS and USER_LOGIN_SUCCESS
    // above and persisted to localStorage via mergeUserInfo({}, data).
    // No intermediate merge, no partial blob — auth and legacy receive
    // identical input, so the auth-reducer projection cannot diverge from
    // the legacy mergeUserInfo result for any auth-dimension field.
    dispatch({
      type: AUTH_LOGIN_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: USER_REGISTER_FAIL,
      payload:
        error.response && error.response.data.detail
          ? error.response.data.detail
          : error.message,
    });
  }
};

export const getUserDetails = (id, options = {}) => async (dispatch) => {
  const { requestId, signal } = options || {};
  try {
    dispatch({
      type: USER_DETAILS_REQUEST,
      meta: { requestId },
    });

    const { data } = await api.get(`/users/${id}/`, { signal });

    dispatch({
      type: USER_DETAILS_SUCCESS,
      payload: data,
      meta: { requestId },
    });
  } catch (error) {
    // Do not dispatch FAIL for aborted/canceled requests
    if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
      return;
    }
    const status = error.response?.status;
    dispatch({
      type: USER_DETAILS_FAIL,
      payload: {
        message:
          error.response && error.response.data.detail
            ? error.response.data.detail
            : error.message,
        status,
      },
      meta: { requestId },
    });
  }
};

export const updateUserProfileInfo = () => async (dispatch, getState) => {
  try {
    dispatch({
      type: USER_UPDATE_PROFILE_INFO_REQUEST,
    });

    const {
      userLogin: { userInfo },
    } = getState();

    const { data } = await api.get(`/users/profile/`);

    const mergedUserInfo = mergeUserInfo(userInfo, data);

    dispatchPersistedUserInfoUpdate(dispatch, mergedUserInfo, {
      pathSuccess: {
        type: USER_UPDATE_PROFILE_INFO_SUCCESS,
        payload: data,
      },
    });
  } catch (error) {
    dispatch({
      type: USER_UPDATE_PROFILE_INFO_FAIL,
      payload:
        error.response && error.response.data.detail
          ? error.response.data.detail
          : error.message,
    });
  }
};

export const updateUserProfile = (user) => async (dispatch, getState) => {
  try {
    dispatch({
      type: USER_UPDATE_PROFILE_REQUEST,
    });

    const {
      userLogin: { userInfo },
    } = getState();

    const { data } = await api.put(`/users/profile/update/`, user);

    const mergedUserInfo = mergeUserInfo(userInfo, data);

    dispatchPersistedUserInfoUpdate(dispatch, mergedUserInfo, {
      pathSuccess: {
        type: USER_UPDATE_PROFILE_SUCCESS,
        payload: data,
      },
    });
  } catch (error) {
    dispatch({
      type: USER_UPDATE_PROFILE_FAIL,
      payload: buildUserProfileUpdateFailPayload(error),
    });
  }
};

export const updateUserProfileImage = (user_image) => async (dispatch, getState) => {
  try {
    dispatch({
      type: USER_UPDATE_PROFILE_IMAGE_REQUEST,
    });

    const {
      userLogin: { userInfo },
    } = getState();

    const { data } = await api.put(`/users/profile/update/image/`, user_image);

    const mergedUserInfo = mergeUserInfo(userInfo, data);

    dispatchPersistedUserInfoUpdate(dispatch, mergedUserInfo, {
      pathSuccess: {
        type: USER_UPDATE_PROFILE_IMAGE_SUCCESS,
        payload: data,
      },
    });
  } catch (error) {
    let payload =
      error.response && error.response.data.detail
        ? error.response.data.detail
        : error.message;

    if (error.response?.status === 404) {
      payload = "Не вдалося завантажити фото профілю.";
    }

    dispatch({
      type: USER_UPDATE_PROFILE_IMAGE_FAIL,
      payload,
    });
  }
};

export const followUser = (public_id) => async (dispatch, getState) => {
  try {
    dispatch({
      type: USER_FOLLOW_REQUEST,
    });

    const {
      userLogin: { userInfo },
    } = getState();

    const { data } = await api.put(
      `/users/profile/follow/`,
      { public_id }
    );

    const mergedUserInfo = mergeUserInfo(userInfo, data);

    dispatchPersistedUserInfoUpdate(dispatch, mergedUserInfo, {
      pathSuccess: {
        type: USER_FOLLOW_SUCCESS,
        payload: data,
      },
    });
  } catch (error) {
    dispatch({
      type: USER_FOLLOW_FAIL,
      payload:
        error.response && error.response.data.detail
          ? error.response.data.detail
          : error.message,
    });
  }
};

export const unfollowUser = (public_id) => async (dispatch, getState) => {
  try {
    dispatch({
      type: USER_UNFOLLOW_REQUEST,
    });

    const {
      userLogin: { userInfo },
    } = getState();

    const { data } = await api.put(
      `/users/profile/unfollow/`,
      { public_id }
    );

    const mergedUserInfo = mergeUserInfo(userInfo, data);

    dispatchPersistedUserInfoUpdate(dispatch, mergedUserInfo, {
      pathSuccess: {
        type: USER_UNFOLLOW_SUCCESS,
        payload: data,
      },
    });
  } catch (error) {
    dispatch({
      type: USER_UNFOLLOW_FAIL,
      payload:
        error.response && error.response.data.detail
          ? error.response.data.detail
          : error.message,
    });
  }
};

export const listUsers = () => async (dispatch) => {
  try {
    dispatch({
      type: USER_LIST_REQUEST,
    });

    const { data } = await api.get(`/users/`);

    dispatch({
      type: USER_LIST_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: USER_LIST_FAIL,
      payload:
        error.response && error.response.data.detail
          ? error.response.data.detail
          : error.message,
    });
  }
};

export const deleteUser = (id) => async (dispatch) => {
  try {
    dispatch({
      type: USER_DELETE_REQUEST,
    });

    const { data } = await api.delete(`/users/delete/${id}/`);

    clearPersistedUserInfo();

    dispatch({
      type: USER_DELETE_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: USER_DELETE_FAIL,
      payload:
        error.response && error.response.data.detail
          ? error.response.data.detail
          : error.message,
    });
  }
};

export const updateUser = (user) => async (dispatch) => {
  try {
    dispatch({
      type: USER_UPDATE_REQUEST,
    });

    const { data } = await api.put(
      `/users/update/${user._id}`,
      user,
    );

    dispatch({
      type: USER_UPDATE_SUCCESS,
    });

    dispatch({
      type: USER_DETAILS_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: USER_UPDATE_FAIL,
      payload:
        error.response && error.response.data.detail
          ? error.response.data.detail
          : error.message,
    });
  }
};

export const userSendMail = () => async (dispatch) => {
  try {
    dispatch({
      type: USER_SEND_MAIL_PROFILE_REQUEST,
    });

    // Backend identifies the account from authenticated request.user.
    // Do not post the Redux user object (or email/token) in the body.
    const { data } = await api.post(
      `/users/email-verification/request/`,
      {},
    );

    dispatch({
      type: USER_SEND_MAIL_PROFILE_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: USER_SEND_MAIL_PROFILE_FAIL,
      payload:
        error.response && error.response.data.detail
          ? error.response.data.detail
          : error.message,
    });
  }
};

/**
 * Narrow in-session reconciliation for an already-fetched /users/profile/ payload.
 * Does not perform a second profile GET. Persists via the existing helper, updates
 * canonical USER_LOGIN_SUCCESS, then AUTH_IDENTITY_PATCH. Preserves access/refresh.
 * On persistence failure: throws without mutating Redux / navigating callers.
 *
 * @param {object} profileData - UserSerializer-shaped profile response
 * @returns {object} merged canonical userInfo
 */
export const reconcileUserInfoFromProfile = (profileData) => (dispatch, getState) => {
  const {
    userLogin: { userInfo },
  } = getState();

  const mergedUserInfo = mergeUserInfo(userInfo || {}, profileData || {});

  dispatchPersistedUserInfoUpdate(dispatch, mergedUserInfo, {
    afterCanonicalUpdate: () => {
      dispatch({
        type: AUTH_IDENTITY_PATCH,
        payload: profileData || {},
      });
    },
  });

  return mergedUserInfo;
};

export const userSendMailChangePass = (email) => async (dispatch) => {
  try {
    dispatch({
      type: USER_SEND_MAIL_CHANGE_PASS_REQUEST,
    });

    const config = {
      headers: {
        'Content-type': 'application/json',
      },
    };

    const { data } = await axios.post(
      `/api/users/password-reset/request/`,
      { email },
      config
    );

    dispatch({
      type: USER_SEND_MAIL_CHANGE_PASS_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: USER_SEND_MAIL_CHANGE_PASS_FAIL,
      payload:
        error.response && error.response.data.detail
          ? error.response.data.detail
          : error.message,
    });
  }
};

export const validateUserProfile = (validate_password) => async (dispatch, getState) => {
  try {
    dispatch({
      type: USER_VALIDATE_PROFILE_REQUEST,
    });

    const {
      userLogin: { userInfo },
    } = getState();

    const { data } = await api.put(
      `/users/email-verification/confirm/`,
      { code: validate_password },
    );

    const mergedUserInfo = mergeUserInfo(userInfo, data);

    dispatchPersistedUserInfoUpdate(dispatch, mergedUserInfo, {
      pathSuccess: {
        type: USER_VALIDATE_PROFILE_SUCCESS,
        payload: data,
      },
      afterCanonicalUpdate: () => {
        // Phase 4B — narrow identity reconciliation into auth slice.
        // The /users/email-verification/confirm/ response is UserSerializer
        // (no access/refresh) — REPLACE-semantic AUTH_LOGIN_SUCCESS would wipe
        // tokens. AUTH_IDENTITY_PATCH is whitelist-restricted to identity-only
        // fields and uses field-presence checks; tokens survive untouched.
        // The is_email_verified flip from false → true propagates via this path.
        dispatch({
          type: AUTH_IDENTITY_PATCH,
          payload: data,
        });
      },
    });
  } catch (error) {
    dispatch({
      type: USER_VALIDATE_PROFILE_FAIL,
      payload:
        error.response && error.response.data.detail
          ? error.response.data.detail
          : error.message,
    });
  }
};

export const changeUserPassword = (email, change_password_code, password) => async (dispatch) => {
  try {
    dispatch({
      type: USER_CHANGE_PASS_PROFILE_REQUEST,
    });

    const config = {
      headers: {
        'Content-type': 'application/json',
      },
    };

    const { data } = await axios.put(
      `/api/users/password-reset/confirm/`,
      { email, change_password_code, password },
      config
    );

    dispatch({
      type: USER_CHANGE_PASS_PROFILE_SUCCESS,
      payload: data,
    });
  } catch (error) {
    dispatch({
      type: USER_CHANGE_PASS_PROFILE_FAIL,
      payload:
        error.response && error.response.data.detail
          ? error.response.data.detail
          : error.message,
    });
  }
};
